import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DerivativeSearchProduct, DerivativeSearchResponse, DerivativeSearchValue, LeverageProductType } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { Observable, catchError, map, of } from 'rxjs';
import { DerivativeType } from '../entities/derivative.type';

@Injectable({
  providedIn: 'root',
})
export class DerivativeSearchInfrastructure {
  readonly #http = inject(HttpClient);
  readonly #baseUri = `${environment.apihost}/apic/derivatesuche`;

  #isDefined = (num: number): boolean => num !== undefined && num !== null;
  #getProducts = (url: string): Observable<DerivativeSearchProduct[]> =>
    this.#http.get<DerivativeSearchResponse>(`${url}`).pipe(
      catchError(() => of(undefined)),
      map((response) => response?.liste),
    );
  #getUrl = (type: DerivativeType, params: URLSearchParams): string => `${this.#baseUri}/${type}?${params.toString()}`;

  public getDiscounts(isin: string, cap: DerivativeSearchValue, duration: DerivativeSearchValue): Observable<DerivativeSearchProduct[]> {
    const params = new URLSearchParams();
    params.set('isin', isin);
    if (cap) {
      const { lower: lowercap, upper: uppercap } = cap;
      this.#isDefined(lowercap) && params.set('lowercap', lowercap.toString());
      this.#isDefined(uppercap) && params.set('uppercap', uppercap.toString());
    }
    if (duration) {
      const { lower: lowerrlz, upper: upperrlz } = duration;
      this.#isDefined(lowerrlz) && params.set('lowerrlz', lowerrlz.toString());
      this.#isDefined(upperrlz) && params.set('upperrlz', upperrlz.toString());
    }

    const url = this.#getUrl('discount', params);
    return this.#getProducts(url);
  }

  public getKnockOuts(isin: string, productType: LeverageProductType, leverage: number): Observable<DerivativeSearchProduct[]> {
    const params = new URLSearchParams();
    params.set('isin', isin);
    params.set('dir', productType === LeverageProductType.CALL ? 'long' : 'short');
    params.set('leverage', leverage === 9999 ? 'max' : leverage.toString());
    const url = this.#getUrl('knockout', params);
    return this.#getProducts(url);
  }

  public getFactors(
    isin: string,
    productType: LeverageProductType,
    factor: DerivativeSearchValue,
    quickMode: boolean,
  ): Observable<DerivativeSearchProduct[]> {
    const params = new URLSearchParams();
    params.set('isin', isin);
    params.set('dir', productType === LeverageProductType.CALL ? 'long' : 'short');
    params.set('quickmode', quickMode.toString());

    if (factor) {
      const { lower: lowerfactor, upper: upperfactor } = factor;
      this.#isDefined(lowerfactor) && params.set('lowerfactor', lowerfactor.toString());
      this.#isDefined(upperfactor) && params.set('upperfactor', upperfactor.toString());
    }

    const url = this.#getUrl('factor', params);
    return this.#getProducts(url);
  }

  public getOS(
    isin: string,
    productType: LeverageProductType,
    strike: DerivativeSearchValue,
    duration: DerivativeSearchValue,
  ): Observable<DerivativeSearchProduct[]> {
    const params = new URLSearchParams();
    params.set('isin', isin);
    params.set('dir', productType === LeverageProductType.CALL ? 'long' : 'short');

    if (strike) {
      const { lower: lowerstrike, upper: upperstrike, sortBy } = strike;
      this.#isDefined(lowerstrike) && params.set('lowerstrike', lowerstrike.toString());
      this.#isDefined(upperstrike) && params.set('upperstrike', upperstrike.toString());
      !!sortBy && params.set('sortBy', sortBy);
    }

    if (duration) {
      const { lower: lowerrlz, upper: upperrlz } = duration;
      this.#isDefined(lowerrlz) && params.set('lowerrlz', lowerrlz.toString());
      this.#isDefined(upperrlz) && params.set('upperrlz', upperrlz.toString());
    }

    const url = this.#getUrl('optionsscheine', params);
    return this.#getProducts(url);
  }
}
